"use client";

import { c as _c } from "react/compiler-runtime";
import { formatUnits } from "viem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconArrowUpRightOutline18 } from "nucleo-ui-outline-18";
import { AnimatedCheckmark } from "@/components/ui/animated-checkmark";
import { Button } from "@/components/ui/button";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useActionExecute } from "@/hooks/useActionExecute";
import { useSimBalance } from "@/hooks/useSimTokenInfo";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import { TabButton } from "@/app/(v2)/wallet-management/_components/shared";
import TokenChainAmountInput from "./form-fields/TokenChainAmountInput";
import type { OrganizationWallet } from "@/types/wallet";
import { POLYGON_CHAIN_ID, POLYGON_PUSD_ADDRESS, POLYGON_USDC_ADDRESS, POLYMARKET_ICON_URL } from "@/lib/polymarket-constants";
import { WalletBalanceLabel } from "./WalletBalanceLabel";
import { ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET } from "@/lib/constants/wallet";
import { formatAddress, getExplorerTxUrl } from "@/lib/utils";
interface PolymarketFundingContentProps {
  recipientAddress: string;
  wallets: OrganizationWallet[];
  onFundingCompleted: () => void;
  onBeforeOpenExternal: () => void;
}
type TabId = "from-wallet" | "external";
export function PolymarketFundingContent(t0) {
  const $ = _c(14);
  const {
    recipientAddress,
    wallets,
    onFundingCompleted,
    onBeforeOpenExternal
  } = t0;
  const [activeTab, setActiveTab] = useState("from-wallet");
  const [hideTabs, setHideTabs] = useState(false);
  let t1;
  let t2;
  if ($[0] !== wallets) {
    t2 = wallets.filter(_temp);
    $[0] = wallets;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  t1 = t2;
  const sourceWallets = t1;
  let t3;
  if ($[2] !== activeTab || $[3] !== hideTabs) {
    t3 = !hideTabs && <div className="border-ds-border-default flex border-b px-5"><TabButton active={activeTab === "from-wallet"} onClick={() => setActiveTab("from-wallet")}>From Wallet</TabButton><TabButton active={activeTab === "external"} onClick={() => setActiveTab("external")}>External</TabButton></div>;
    $[2] = activeTab;
    $[3] = hideTabs;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== activeTab || $[6] !== onBeforeOpenExternal || $[7] !== onFundingCompleted || $[8] !== recipientAddress || $[9] !== sourceWallets) {
    t4 = <div className="min-w-0 px-5 pb-5 pt-4">{activeTab === "from-wallet" ? <FromWalletTab recipientAddress={recipientAddress} wallets={sourceWallets} onFundingCompleted={onFundingCompleted} onBusyChange={setHideTabs} /> : <ExternalTab recipientAddress={recipientAddress} onBeforeOpenExternal={onBeforeOpenExternal} onFundingCompleted={onFundingCompleted} />}</div>;
    $[5] = activeTab;
    $[6] = onBeforeOpenExternal;
    $[7] = onFundingCompleted;
    $[8] = recipientAddress;
    $[9] = sourceWallets;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex min-w-0 flex-col overflow-hidden">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp(w) {
  return w.accountType !== ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET;
}
function FromWalletTab({
  recipientAddress,
  wallets,
  onFundingCompleted,
  onBusyChange
}: {
  recipientAddress: string;
  wallets: OrganizationWallet[];
  onFundingCompleted: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [amountWei, setAmountWei] = useState("");
  const [token, setToken] = useState({
    address: POLYGON_USDC_ADDRESS,
    chainId: POLYGON_CHAIN_ID,
    symbol: "USDC",
    decimals: 6
  });
  const [isInsufficient, setIsInsufficient] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState<string>(() => (wallets.find(w => w.isDefault) ?? wallets[0])?.id ?? "");
  const selectedWallet = useMemo(() => wallets.find(w_0 => w_0.id === selectedWalletId) ?? wallets[0], [wallets, selectedWalletId]);
  const walletId = selectedWallet?.id;
  const sourceAddress = selectedWallet?.address;
  const {
    data: balanceData
  } = useSimBalance(sourceAddress);
  useEffect(() => {
    setHasAutoSelected(false);
  }, [sourceAddress]);
  useEffect(() => {
    if (hasAutoSelected || !balanceData?.balances?.length) return;
    const sorted = [...balanceData.balances].sort((a, b) => (b.value_usd ?? 0) - (a.value_usd ?? 0));
    const best = sorted[0];
    if (best && (best.value_usd ?? 0) > 0) {
      setToken({
        address: best.address,
        chainId: best.chain_id,
        symbol: best.symbol,
        decimals: best.decimals
      });
      setHasAutoSelected(true);
    }
  }, [balanceData, hasAutoSelected]);
  const handleTokenChange = useCallback((address: string, chainId: number, symbol: string, decimals: number) => {
    setToken({
      address,
      chainId,
      symbol,
      decimals
    });
    setHasAutoSelected(true);
  }, []);
  const {
    execute: executeDeposit,
    isLoading: isDepositing,
    error: depositError
  } = useActionExecute("polymarket-deposit");
  const {
    execute: executeSend,
    isLoading: isSending,
    error: sendError
  } = useActionExecute("send-erc20-token");
  const isPusdOnPolygon = token.chainId === POLYGON_CHAIN_ID && token.address.toLowerCase() === POLYGON_PUSD_ADDRESS.toLowerCase();
  const isExecuting = isDepositing || isSending;
  const actionError = depositError || sendError;
  const hasAmount = useMemo(() => {
    if (!amountWei) return false;
    try {
      return BigInt(amountWei) > 0n;
    } catch {
      return false;
    }
  }, [amountWei]);
  const canDeposit = hasAmount && !isInsufficient && !!walletId && !isExecuting && !isComplete;
  const prevBusyRef = useRef(false);
  useEffect(() => {
    const isBusy = isExecuting || isComplete;
    if (prevBusyRef.current !== isBusy) {
      prevBusyRef.current = isBusy;
      onBusyChange(isBusy);
    }
  }, [isExecuting, isComplete, onBusyChange]);
  const submittingRef = useRef(false);
  const handleDeposit = useCallback(async () => {
    if (!walletId || !canDeposit || submittingRef.current) return;
    submittingRef.current = true;
    try {
      let result: Record<string, unknown>;
      if (isPusdOnPolygon) {
        result = (await executeSend({
          chainId: POLYGON_CHAIN_ID,
          tokenAddress: POLYGON_PUSD_ADDRESS,
          recipientAddress,
          amount: amountWei
        }, walletId)) as Record<string, unknown>;
      } else {
        result = (await executeDeposit({
          amount: formatUnits(BigInt(amountWei), token.decimals),
          chainId: token.chainId,
          tokenAddress: token.address,
          recipientAddress,
          amountRaw: amountWei
        }, walletId)) as Record<string, unknown>;
      }
      setIsComplete(true);
      const hash = result?.transactionHash as string | undefined;
      if (hash) setTxHash(hash);
    } catch {
      // useActionExecute exposes the error via actionError
    } finally {
      submittingRef.current = false;
    }
  }, [walletId, canDeposit, isPusdOnPolygon, executeSend, executeDeposit, token, recipientAddress, amountWei]);
  const fundedDisplayAmount = useMemo(() => {
    if (!amountWei) return "";
    try {
      return formatUnits(BigInt(amountWei), token.decimals);
    } catch {
      return "";
    }
  }, [amountWei, token.decimals]);
  if (isExecuting) {
    return <div className="flex flex-col items-center gap-4 py-10">
        <PixelLoader size="xl" color="blue" />
        <p className="body-semibold">Funding in progress</p>
        <p className="label-xs text-ds-text-muted text-center">
          Converting {fundedDisplayAmount} {token.symbol} to pUSD for your Polymarket wallet...
        </p>
      </div>;
  }
  if (isComplete) {
    return <div className="space-y-5">
        <div className="flex items-center gap-4">
          <AnimatedCheckmark className="h-10 w-10 shrink-0" />
          <div>
            <h2 className="heading-lg">Funding Successful</h2>
            <p className="text-ds-text-muted mt-0.5 text-sm">
              {fundedDisplayAmount} {token.symbol} converted to pUSD for Polymarket wallet
            </p>
          </div>
        </div>
        <div className="border-ds-border-default bg-ds-surface-sunken rounded-lg border px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-ds-text-muted text-xs">Polymarket wallet balance</span>
            <WalletBalanceLabel address={recipientAddress} />
          </div>
        </div>
        <div className="flex gap-3">
          {txHash && <a href={getExplorerTxUrl(token.chainId, txHash)} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="white" className="w-full">
                <span className="flex items-center justify-center gap-2 text-sm">
                  <IconArrowUpRightOutline18 className="h-4 w-4" />
                  View on Explorer
                </span>
              </Button>
            </a>}
          <Button variant="brand" onClick={() => onFundingCompleted()} className="flex-1">
            Done
          </Button>
        </div>
      </div>;
  }
  if (wallets.length === 0) {
    return <div className="flex flex-col items-center gap-2 py-6">
        <p className="body-sm text-ds-text-muted text-center">
          You need at least one funding wallet. Use the External tab to fund from an outside wallet.
        </p>
      </div>;
  }
  return <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label className="field-label">Source Wallet</label>
        {wallets.length > 1 ? <Select value={selectedWalletId} onValueChange={setSelectedWalletId}>
            <SelectTrigger>
              <SelectValue placeholder="Select wallet" />
            </SelectTrigger>
            <SelectContent>
              {wallets.map(w_1 => <SelectItem key={w_1.id} value={w_1.id}>
                  <span className="flex items-center gap-2">
                    <span>{w_1.name}</span>
                    <span className="text-ds-text-muted text-xs">{formatAddress(w_1.address)}</span>
                    <WalletBalanceLabel address={w_1.address} />
                  </span>
                </SelectItem>)}
            </SelectContent>
          </Select> : selectedWallet ? <div className="border-ds-border-default bg-ds-surface-card flex items-center gap-2 rounded-lg border px-3 py-2">
            <span className="text-ds-text-primary text-sm">{selectedWallet.name}</span>
            <span className="text-ds-text-muted text-xs">{formatAddress(selectedWallet.address)}</span>
            <WalletBalanceLabel address={selectedWallet.address} />
          </div> : null}
      </div>

      <div className="flex items-center gap-2 px-1">
        <img src={POLYMARKET_ICON_URL} alt="" className="h-4 w-4 shrink-0 rounded-full" />
        <span className="text-ds-text-muted text-xs">To Polymarket wallet {formatAddress(recipientAddress)}</span>
        <WalletBalanceLabel address={recipientAddress} />
      </div>

      <TokenChainAmountInput amount={amountWei} onAmountChange={setAmountWei} tokenAddress={token.address} chainId={token.chainId} onTokenChange={handleTokenChange} label="Amount" popoverModal workflowWalletAddress={sourceAddress} onInsufficientBalance={setIsInsufficient} />

      {actionError && <p className="label-xs text-red-500">{actionError.message}</p>}

      <Button onClick={handleDeposit} disabled={!canDeposit} className="w-full">
        Fund Wallet
      </Button>
    </div>;
}
function ExternalTab(t0) {
  const $ = _c(10);
  const {
    recipientAddress,
    onBeforeOpenExternal,
    onFundingCompleted
  } = t0;
  const setModalOpen = useModalStore(_temp2);
  const setModalContentType = useModalStore(_temp3);
  let t1;
  if ($[0] !== onBeforeOpenExternal || $[1] !== onFundingCompleted || $[2] !== recipientAddress || $[3] !== setModalContentType || $[4] !== setModalOpen) {
    t1 = () => {
      onBeforeOpenExternal();
      setModalContentType({
        type: "anySpendDeposit",
        recipientAddress,
        destinationTokenAddress: POLYGON_PUSD_ADDRESS,
        destinationTokenChainId: POLYGON_CHAIN_ID,
        actionLabel: "Fund Polymarket wallet with pUSD",
        onSuccess: onFundingCompleted,
        allowDirectTransfer: true,
        showFiatOption: false
      });
      setModalOpen(true);
    };
    $[0] = onBeforeOpenExternal;
    $[1] = onFundingCompleted;
    $[2] = recipientAddress;
    $[3] = setModalContentType;
    $[4] = setModalOpen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleOpenAnySpend = t1;
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="body-sm text-ds-text-primary">Fund from any external wallet or exchange into your Polymarket wallet.</p>;
    t3 = <p className="label-xs text-ds-text-muted">Supports cross-chain transfers from any network. Funds are bridged to pUSD on Polygon automatically.</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleOpenAnySpend) {
    t4 = <div className="flex flex-col gap-4 py-2">{t2}{t3}<Button variant="white" onClick={handleOpenAnySpend} className="w-full">Fund from external wallet</Button></div>;
    $[8] = handleOpenAnySpend;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp3(state_0) {
  return state_0.setB3ModalContentType;
}
function _temp2(state) {
  return state.setB3ModalOpen;
}